import React, { memo } from 'react';
import { Row, Col } from 'react-bootstrap';
import { formatInTimeZone } from 'date-fns-tz';
import officialscoresheetStyles from '../../officialscoresheet.module.css';
import { TeamBreakdownScoreSheetTable } from '../shared/tables';
import { prettyTeamScoring } from '../../../../utilities/scoring';
import { Clock } from '../shared/clock';

const {
  header,
  row,
  virtiusTitle,
  sessionInfo,
  sessionInfoLabel,
  sessionInfoValue,
  body,
  footer,
  subFooter,
  timestamp,
} = officialscoresheetStyles;

function TeamBreakdownScoresheetHeader({
  sessionName,
  date,
  timezone,
  location,
  altTeamScoring,
  combineKey,
  combineKeys,
  combineSession,
  combinedSessions,
}) {
  return (
    <section className={header}>
      <h1>
        <span className={virtiusTitle}>
          <span>V</span>irtius&nbsp;
        </span>
        <span style={{ fontWeight: '700' }}>Score Sheet</span>
      </h1>
      <h2>
        TEAM BREAKDOWN
        {altTeamScoring ? ` (${prettyTeamScoring(altTeamScoring)})` : null}
        {combineKey || combineKeys.length > 0 ? ` (Combined Sessions)` : null}
      </h2>
      <section>
        <Row className={row}>
          <Col className={sessionInfo}>
            <div className={sessionInfoLabel}>
              Session{combineKey || combineKeys.length > 0 ? '(s)' : null}
              :&nbsp;
            </div>
            <div className={sessionInfoValue}>
              {sessionName}
              {combineKey ? ` + ${combineSession?.session?.name}` : null}
              {combineKeys.length > 0
                ? ` + ${combinedSessions
                    .filter((s) => s)
                    .map((s) => s.name)
                    .join(' + ')}`
                : null}
            </div>
          </Col>
        </Row>
        <Row className={row}>
          <Col className={sessionInfo}>
            <div className={sessionInfoLabel}>Date: </div>
            <div className={sessionInfoValue}>{`${formatInTimeZone(
              new Date(date),
              timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
              "M'/'dd'/'yyyy"
            )}`}</div>
          </Col>
        </Row>
        <Row className={row}>
          <Col className={sessionInfo}>
            <div className={sessionInfoLabel}>Location: </div>
            <div className={sessionInfoValue}>{location}</div>
          </Col>
        </Row>
      </section>
    </section>
  );
}

function TeamBreakdownScoresheetBody({
  teams,
  apparatusArray,
  teamsByAthleteId,
}) {
  return (
    <main className={body}>
      {teams.map((t, i) => {
        return (
          <Row
            className={row}
            key={`team_${i}`}
            style={{ marginBottom: i === 0 ? '20pt' : null }}
          >
            <Col>
              <TeamBreakdownScoreSheetTable
                team={t}
                apparatus={apparatusArray}
                teamsByAthleteId={teamsByAthleteId}
              />
            </Col>
          </Row>
        );
      })}
    </main>
  );
}

function BasicScoresheetFooter({
  page,
  timezone,
  singleSheetOnly,
  sheetOptionsLength,
}) {
  const copyrightTimePageFooter = (page) => {
    return (
      <section className={subFooter}>
        <Row>
          <Col className={timestamp}>
            <span>
              © {new Date().getFullYear()} Virtius&nbsp;&nbsp;»&nbsp;&nbsp;
            </span>
            <Clock timezone={timezone} />
            <span>
              &nbsp;&nbsp;»&nbsp;&nbsp;Page {singleSheetOnly ? 1 : page} of{' '}
              {singleSheetOnly ? 1 : sheetOptionsLength}
            </span>
          </Col>
        </Row>
      </section>
    );
  };

  return <section className={footer}>{copyrightTimePageFooter(page)}</section>;
}

export const TeamBreakdownScoreSheet = memo(_TeamBreakdownScoreSheet);

function _TeamBreakdownScoreSheet({
  page,
  sessionName,
  date,
  timezone,
  location,
  altTeamScoring,
  combineKey,
  combineKeys,
  combineSession,
  combinedSessions,
  teams,
  apparatusArray,
  teamsByAthleteId,
  singleSheetOnly,
  sheetOptionsLength,
  teamAIndex,
  teamBIndex,
  options,
}) {
  const indices = (options && options.split('_')) || '';
  let aIndex = indices.length > 0 ? parseInt(indices[0]) ?? null : null;
  let bIndex = indices.length > 1 ? parseInt(indices[1]) ?? null : null;

  // Override with custom settings
  if (teamAIndex !== null) {
    aIndex = teamAIndex;
  }

  if (teamBIndex !== null) {
    bIndex = teamBIndex;
  }

  const teamA = aIndex < teams.length ? teams[aIndex] : null;
  const teamB = bIndex < teams.length ? teams[bIndex] : null;

  return (
    <>
      <TeamBreakdownScoresheetHeader
        sessionName={sessionName}
        date={date}
        timezone={timezone}
        location={location}
        altTeamScoring={altTeamScoring}
        combineKey={combineKey}
        combineKeys={combineKeys}
        combineSession={combineSession}
        combinedSessions={combinedSessions}
      />
      <TeamBreakdownScoresheetBody
        teams={[teamA, teamB]}
        apparatusArray={apparatusArray}
        teamsByAthleteId={teamsByAthleteId}
      />
      <BasicScoresheetFooter
        page={page}
        timezone={timezone}
        singleSheetOnly={singleSheetOnly}
        sheetOptionsLength={sheetOptionsLength}
      />
    </>
  );
}
